import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  fusionStoreToken,
  TaskWorkspaceStore,
} from '../../../../core/h3ml/state/task-workspace-store';
import { UndoService } from '../../../../core/outputs/annotation/annotation-shared/toolbars/static-toolbar/undo.service';
import { AttributeHelper } from '../../../../core/shared/attribute-helper/attribute-helper';
import { getAttributeHelper } from '../../../../core/shared/attribute-helper/get-attribute-helper';
import { AttributeContextMenuService } from '../../../../core/shared/context-menu/attribute-context-menu.service';
import { AnnotationState } from '../../../../cross-team-shared/annotation/shared/state/annotation-state';
import {
  getShapeForEventTracking,
  isShapeStore,
  Tag,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { DebouncedEventTracker } from '../../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { assertStore } from '../../../../cross-team-shared/misc-utils';
import {
  getAttributeValue,
  getAttributeValue$,
  setAttribute,
} from '../../../../outputs/annotation/annotation-shared/utils/attribute-store-helper';
import { FrameSpecificTag } from '../../../../team-apis/otterdam/answers/shape-typings';
import { Attribute } from '../../../../team-apis/otterdam/taxonomy';
import { EventTrackingService } from '../../../event-tracking/event-tracking.service';

@Directive()
export abstract class AbstractAttributeInputComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input({ required: true }) attribute!: Attribute;
  @Input({ required: true }) annotationStore!: Store<AnnotationState>;
  @ViewChild('inputContainer', { static: false }) inputContainer!: ElementRef;

  attributeValue$!: Observable<Tag>;
  attributeLabel$!: Observable<string>;
  isFocused = false;
  form!: FormGroup<{ input: FormControl<Tag> }>;
  protected formBuilder = inject(FormBuilder);
  protected undoService = inject(UndoService);
  protected attributeHelper!: AttributeHelper;
  protected store = inject(TaskWorkspaceStore);

  protected isReadOnly$!: Observable<boolean>;
  private contextMenuService = inject(AttributeContextMenuService);
  private fusionStore = inject(fusionStoreToken);
  private eventTracker = new DebouncedEventTracker(
    { id: 'change_answer', bucket: 'tag_selection', sub_bucket: 'annotation' },
    inject(EventTrackingService),
  );

  @HostListener('contextmenu', ['$event'])
  openContextMenu(event: MouseEvent) {
    event.preventDefault();
    const tagStore = this.annotationStore('dynamic_tags')(this.attribute.name);
    const frameNumber = this.fusionStore.state().frameNumber;
    if (
      this.isFocused ||
      !this.attribute.isDynamic ||
      !hasDynamicTagAtFrame(tagStore, frameNumber)
    ) {
      return;
    }

    assertStore(tagStore, 'tagStore must be defined');
    return this.contextMenuService.open(
      [event.clientX, event.clientY],
      tagStore,
      frameNumber,
    );
  }

  ngOnInit() {
    this.attributeHelper = getAttributeHelper(this.attribute.__type);
    this.form = this.formBuilder.group({
      input: new FormControl<Tag>(
        this.attributeHelper.getDefaultOptionValue(this.attribute),
        { nonNullable: true },
      ),
    });

    const frameNumberStore = this.fusionStore('frameNumber');

    this.attributeValue$ = this.getInput$('annotationStore').pipe(
      switchMap((annotationStore) =>
        getAttributeValue$(this.attribute, annotationStore, frameNumberStore),
      ),
    );

    this.isReadOnly$ = this.store('readOnly').$;
    this.attributeLabel$ = this.buildDisplayValue$();
  }

  clearValue() {
    this.form.controls.input.reset(undefined, { emitEvent: false });
    this.closeAndSave('reset_button');
  }

  protected closeAndSave(
    source: 'reset_button' | 'mouse' | 'keyboard_shortcut' = 'mouse',
  ) {
    this.isFocused = false;
    this.setAttribute(this.form.controls.input.value, source);
  }

  protected setAttribute(
    value: Tag,
    source: 'reset_button' | 'mouse' | 'keyboard_shortcut' = 'mouse',
  ) {
    const currentAttributeValue = getAttributeValue(
      this.attribute,
      this.annotationStore,
      this.fusionStore.state().frameNumber,
    );

    // avoid saving and pushing a new undo state with the same value
    if (currentAttributeValue !== value) {
      setAttribute(
        this.annotationStore,
        this.attribute,
        value,
        this.fusionStore.state().frameNumber,
      );
      this.eventTracker.trackWithDebounce({
        source,
        parent_context: this.getParentContext(),
      });
      this.undoService.pushCurrentState();
    }
  }

  private getParentContext() {
    const annotationStore = this.annotationStore;
    if (isShapeStore(annotationStore)) {
      const { outputName } = annotationStore.state();
      assert(outputName, 'must have outputName for objects');
      const output = this.store.state().outputInfoByName[outputName].output;

      return {
        output_id: output.id,
        output_type: output.type,
        shapes: [getShapeForEventTracking(annotationStore.state())],
      };
    }

    return {
      object: annotationStore.state(),
    };
  }

  private buildDisplayValue$() {
    return this.attributeValue$.pipe(
      map((value) => {
        this.form.setValue({ input: value || '' }, { emitEvent: false });
        return this.attributeHelper.getLabel(value, this.attribute);
      }),
    );
  }
}

function hasDynamicTagAtFrame(
  dynamicTagStore: Store<FrameSpecificTag[] | undefined>,
  currentFrameNumber: number,
): dynamicTagStore is Store<FrameSpecificTag[]> {
  if (!isDefined(dynamicTagStore.state())) {
    return false;
  }
  assertStore(dynamicTagStore, '');
  return dynamicTagStore
    .state()
    .some((tag) => tag.frame_number === currentFrameNumber);
}
